import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, MapPin, Users, Percent } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import api, { formatCurrency } from '../services/api';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#f59e0b', '#ec4899'];

export default function AdvancedAnalytics() {
  const [data, setData] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [clients, setClients] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, routeRes, clientRes] = await Promise.allSettled([
          api.get('/analytics/dashboard'),
          api.get('/analytics/route-profitability'),
          api.get('/analytics/client-profitability'),
        ]);
        if (dashRes.status === 'fulfilled') setData(dashRes.value.data);
        if (routeRes.status === 'fulfilled') setRoutes(routeRes.value.data);
        if (clientRes.status === 'fulfilled') setClients(clientRes.value.data);
      } catch { toast.error('Failed to load analytics'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="advanced-analytics">
      <div><h1 className="font-heading font-bold text-2xl text-slate-900">Advanced Analytics</h1><p className="text-sm text-slate-500">Business intelligence, route profitability, and client insights</p></div>

      <div className="flex gap-2 border-b border-slate-200">
        {[['overview', 'Business Overview'], ['routes', 'Route Profitability'], ['clients', 'Client Analysis'], ['expenses', 'Expense Analytics']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500'}`}>{l}</button>
        ))}
      </div>

      {tab === 'overview' && data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Collection Efficiency</p><p className="font-mono font-bold text-2xl text-green-600">{data.collection_efficiency}%</p></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Total Billed</p><p className="font-mono font-bold text-xl">Rs. {formatCurrency(data.total_billed)}</p></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Total Collected</p><p className="font-mono font-bold text-xl text-green-600">Rs. {formatCurrency(data.total_collected)}</p></div>
            <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs text-slate-500">Outstanding</p><p className="font-mono font-bold text-xl text-red-600">Rs. {formatCurrency(data.total_billed - data.total_collected)}</p></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">Revenue Trend</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.revenue_trend}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip formatter={v => `Rs. ${formatCurrency(v)}`} /><Bar dataKey="revenue" fill="#f97316" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">GST Collection Trend</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.gst_trends}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip formatter={v => `Rs. ${formatCurrency(v)}`} /><Legend /><Line type="monotone" dataKey="cgst" stroke="#6366f1" name="CGST" /><Line type="monotone" dataKey="sgst" stroke="#8b5cf6" name="SGST" /><Line type="monotone" dataKey="igst" stroke="#a855f7" name="IGST" /></LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">Shipment Volume</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.shipment_volume}><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">Payment Status</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={data.payment_status} dataKey="amount" nameKey="status" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {data.payment_status?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={v => `Rs. ${formatCurrency(v)}`} /></PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-3">Top Clients</h3>
            <div className="space-y-2">{data.top_clients?.slice(0, 5).map((c, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <div className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">{i + 1}</span><div><p className="font-medium text-sm">{c.client}</p><p className="text-xs text-slate-400">{c.invoices} invoices | Avg: Rs. {formatCurrency(c.avg_invoice)}</p></div></div>
                <p className="font-mono font-bold">Rs. {formatCurrency(c.revenue)}</p>
              </div>
            ))}</div>
          </div>
        </div>
      )}

      {tab === 'routes' && routes && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">Route</th><th className="p-3 text-right">Shipments</th><th className="p-3 text-right">Est. Revenue</th><th className="p-3 text-right">Avg/Shipment</th></tr></thead>
            <tbody>{routes.routes?.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3"><span className="font-medium">{r.origin}</span><span className="text-slate-400 mx-2">-&gt;</span><span className="font-medium">{r.destination}</span></td>
                <td className="p-3 text-right font-mono">{r.shipments}</td>
                <td className="p-3 text-right font-mono">{formatCurrency(r.estimated_revenue)}</td>
                <td className="p-3 text-right font-mono text-green-600">{formatCurrency(r.avg_revenue_per_shipment)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab === 'clients' && clients && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">Client</th><th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Invoices</th><th className="p-3 text-right">Avg Invoice</th><th className="p-3 text-right">Payment Rate</th><th className="p-3 text-center">Status</th></tr></thead>
            <tbody>{clients.clients?.map((c, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3"><p className="font-medium">{c.name}</p><p className="text-xs text-slate-400">{c.first_invoice} to {c.last_invoice}</p></td>
                <td className="p-3 text-right font-mono font-medium">{formatCurrency(c.total_revenue)}</td>
                <td className="p-3 text-right">{c.invoice_count}</td>
                <td className="p-3 text-right font-mono">{formatCurrency(c.avg_invoice)}</td>
                <td className="p-3 text-right"><span className={`font-mono font-medium ${c.payment_rate >= 80 ? 'text-green-600' : c.payment_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{c.payment_rate}%</span></td>
                <td className="p-3 text-center"><span className="text-xs">{c.paid} paid / {c.pending} pending / {c.overdue} overdue</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab === 'expenses' && data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-3">Expense Breakdown</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart><Pie data={data.expense_breakdown} dataKey="total" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={({ name }) => name}>
                {data.expense_breakdown?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={v => `Rs. ${formatCurrency(v)}`} /></PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-3">Expense Details</h3>
            <div className="space-y-2">{data.expense_breakdown?.map((e, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><p className="font-medium text-sm capitalize">{e.category?.replace(/_/g, ' ')}</p></div>
                <div className="text-right"><p className="font-mono font-bold text-sm">Rs. {formatCurrency(e.total)}</p><p className="text-xs text-slate-400">{e.count} entries</p></div>
              </div>
            ))}</div>
          </div>
        </div>
      )}
    </div>
  );
}
